import React, {useMemo, useRef} from 'react';

import UniversalBlockGlobalClientStates from 'insights/universal-blocks/UniversalBlockGlobalClientStates';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import Menu from 'ui-library/menus/Menu';

import BaseBlockOverflowDropdown from './BaseBlockOverflowDropdown';
import {SHARED_MENU_PROPS} from './BlockOverflowMenuConstants';
import getAddToWorklistOption from './getAddToWorklistOption';
import getCopyAsTSVOption from './getCopyAsTSVOption';
import getDownloadAsCSVOption, {ExportBlockFn} from './getDownloadAsCSVOption';
import getExecuteRequestOption from './getExecuteRequestOption';

interface Actions {
  exportBlock: ExportBlockFn;
}

interface Props {
  actions: Actions;
  blockViewStateId: string;
  className: string;
  runQueryState: RunQueryState;
}

/**
 * Overflow menu for block editor
 */
export default function AnalyticsBlockEditorOverflowDropdown({
  actions,
  className,
  blockViewStateId,
  runQueryState,
}: Props): JSX.Element {
  const [showRawOutput] =
    UniversalBlockGlobalClientStates.showRawOutputState.useGlobalClientState(blockViewStateId);
  const [sortState] = UniversalBlockGlobalClientStates.sortState.useGlobalClientState(blockViewStateId);
  const tsvTextRef = useRef<string | null>(null);

  const legacyRunQueryState = useMemo(
    () => convertRunQueryStateToLegacyState(runQueryState),
    [runQueryState]
  );

  const downloadAsCSVOption = useMemo(
    () =>
      getDownloadAsCSVOption({
        actions,
        runQueryState: legacyRunQueryState,
        showRawOutput,
      }),
    [actions, legacyRunQueryState, showRawOutput]
  );

  const copyAsTSVOption = useMemo(
    () =>
      getCopyAsTSVOption({
        runQueryState: legacyRunQueryState,
        showRawOutput,
        sortState,
        tsvTextRef,
      }),
    [legacyRunQueryState, showRawOutput, sortState, tsvTextRef]
  );

  const addToWorklistOption = useMemo(
    () =>
      getAddToWorklistOption({
        runQueryState: legacyRunQueryState,
      }),
    [legacyRunQueryState]
  );

  const executeRequestOption = useMemo(
    () =>
      getExecuteRequestOption({
        runQueryState: legacyRunQueryState,
      }),
    [legacyRunQueryState]
  );

  return (
    <BaseBlockOverflowDropdown
      className={className}
      menu={
        <Menu {...SHARED_MENU_PROPS}>
          {downloadAsCSVOption}
          {copyAsTSVOption}
          {addToWorklistOption}
          {executeRequestOption}
        </Menu>
      }
    />
  );
}
